import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import { APP_ROUTES } from '@/const/appRoutes'
import type { PaymentFilters, SetupIntent } from '@/types/subscription'

const buildPaymentsQueryString = (filters: PaymentFilters) => {
    const params = new URLSearchParams()

    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.append(key, value.toString())
        }
    })

    const queryString = params.toString()
    return queryString ? `?${queryString}` : ''
}

const buildManageCheckoutReturnUrls = () => {
    const baseUrl = `${window.location.origin}${APP_ROUTES.TUITION_MANAGE}`
    return {
        success_url: `${baseUrl}?session_id={CHECKOUT_SESSION_ID}`,
        cancel_url: `${baseUrl}?cancelled=true`
    }
}

export const subscriptionService = {
    // Plans / offers
    async getAssignedPlans() {
        return api.get(API_ROUTES.SUBSCRIPTION_ASSIGNED_PLANS)
    },

    async getMyOffer() {
        return api.get(API_ROUTES.SUBSCRIPTION_MY_OFFER)
    },

    async getMyProductPurchases() {
        return api.get(API_ROUTES.SUBSCRIPTION_MY_PRODUCT_PURCHASES)
    },

    // Customers
    async getCustomers() {
        return api.get(API_ROUTES.SUBSCRIPTION_CUSTOMERS)
    },

    async createCustomer(payload: { user: string | null; email: string }) {
        return api.post(API_ROUTES.SUBSCRIPTION_CUSTOMERS, payload)
    },

    // Payment Methods
    async getPaymentMethods() {
        return api.get(API_ROUTES.SUBSCRIPTION_PAYMENT_METHODS)
    },

    async createSetupIntent(customerId: string): Promise<SetupIntent> {
        const response = await api.post(API_ROUTES.SUBSCRIPTION_SETUP_INTENT, {
            customer_id: customerId
        })
        return response.data
    },

    async attachPaymentMethod(paymentMethodId: string, customerId: string) {
        return api.post(API_ROUTES.SUBSCRIPTION_ATTACH_PAYMENT_METHOD, {
            payment_method_id: paymentMethodId,
            customer_id: customerId
        })
    },

    async setDefaultPaymentMethod(paymentMethodId: string, customerId: string) {
        return api.post(API_ROUTES.SUBSCRIPTION_SET_DEFAULT_PAYMENT_METHOD, {
            payment_method_id: paymentMethodId,
            customer_id: customerId
        })
    },

    async deletePaymentMethod(paymentMethodId: number) {
        return api.delete(`${API_ROUTES.SUBSCRIPTION_PAYMENT_METHODS}${paymentMethodId}/`)
    },

    // Subscriptions
    async getCurrentSubscriptions() {
        return api.get(API_ROUTES.SUBSCRIPTION_CURRENT)
    },

    async createSubscription(payload: { plan_id: number; trial_period_days?: number }) {
        return api.post(API_ROUTES.SUBSCRIPTION_CREATE, payload)
    },

    // Checkout
    async createSubscriptionCheckoutSession(payload: Record<string, unknown>) {
        return api.post(API_ROUTES.SUBSCRIPTION_CHECKOUT_CREATE_SUBSCRIPTION, payload)
    },

    async createProductPurchaseCheckoutSession(payload: {
        product_id: string
        success_url: string
        cancel_url: string
    }) {
        return api.post(API_ROUTES.SUBSCRIPTION_CHECKOUT_CREATE_PRODUCT_PURCHASE, payload)
    },

    async createSetupCardSession() {
        const payload = buildManageCheckoutReturnUrls()
        return api.post(API_ROUTES.SUBSCRIPTION_CHECKOUT_SETUP_CARD, payload)
    },

    async getCheckoutSession(sessionId: string) {
        return api.get(`${API_ROUTES.SUBSCRIPTION_CHECKOUT_SESSION}?session_id=${sessionId}`)
    },

    async retryPayment() {
        const payload = buildManageCheckoutReturnUrls()
        return api.post(API_ROUTES.SUBSCRIPTION_RETRY_PAYMENT, payload)
    },

    // Payments
    async getPayments(queryString: string) {
        return api.get(`${API_ROUTES.SUBSCRIPTION_PAYMENTS}${queryString}`)
    },

    async getPaymentsByPage(page: number, pageSize: number) {
        return api.get(
            `${API_ROUTES.SUBSCRIPTION_PAYMENTS}?page=${page}&page_size=${pageSize}`
        )
    },

    // Access Status
    async checkAccessStatus() {
        return api.get(API_ROUTES.SUBSCRIPTION_ACCESS_STATUS)
    },

    // Helpers exposed for store usage if needed, or kept internal
    buildPaymentsQueryString
}
